import { assocIf } from '@btime/helpers'
import { compose, is, map, prop, propEq, toPairs, zip } from 'ramda'

import { getComplementaryOptions, useComplementaryOptionsGenerator } from './complementaryOptions'

export const useFormStateFormatter = form => {
  const getComplementaryOptions = useComplementaryOptionsGenerator()

  return {
    ...form,
    payload: compose(
      prop('payload'), //
      formatInsertSOsPayload,
      formatInsertSOPayload,
      formatInsertPlacePayload,
      formatPayloadHeaders,
      formatSlackDirectMessagePayload,
      formatInterpolatedPayload(getComplementaryOptions)
    )(form)
  }
}

const formatSlackDirectMessagePayload = form => {
  if (form?.action !== 'slack_direct_message') return form

  const email = String(form?.payload?.user?.email).replace(
    `field_id=${form?.payload?.user?.field_origin_email?.id}}}`,
    'field_id=#field_id#}}'
  )

  return {
    ...form,
    payload: {
      ...form?.payload,
      user: {
        ...form.payload?.user,
        email
      }
    }
  }
}

const formatInsertSOPayload = form => {
  const supported = ['insert_service_order', 'schedule_insert_service_order']
  if (!supported.includes(form?.action)) return form

  const payload = form?.payload || {}

  return {
    ...form,
    payload: {
      ...payload, //
      ...assocIf(!!payload?.user, 'user_or_group', { ...payload?.user, __typename: 'User' }),
      ...assocIf(!!payload?.group, 'user_or_group', { ...payload?.group, __typename: 'UserGroup' })
    }
  }
}

const formatInsertSOsPayload = form => {
  const supported = [
    'insert_service_orders',
    'create_bulk_service_orders_with_parent',
    'schedule_insert_service_orders'
  ]
  if (!supported.includes(form?.action)) return form
  const payload = form?.payload || []

  return {
    ...form,
    payload: payload.map(item => ({
      ...item,

      ...assocIf(!!item?.user, 'user_or_group', { ...item?.user, __typename: 'User' }),
      ...assocIf(!!item?.group, 'user_or_group', { ...item?.group, __typename: 'UserGroup' })
    }))
  }
}

const formatInsertPlacePayload = form => {
  if (form?.action !== 'insert_place') return form

  const payload = form?.payload || {}

  return {
    ...form,
    payload: {
      ...payload // TODO: Set place levels or create generic place level inputs
    }
  }
}

const formatPayloadHeaders = form => {
  const payload = form.payload || {}
  if (!payload?.headers) return form

  return {
    ...form,
    payload: {
      ...payload,
      headers: compose(
        map(([key, value]) => ({ key, value })), //
        toPairs
      )(payload.headers)
    }
  }
}

const formatInterpolatedPayload = getOptions => form => {
  if (form?.action === 'create_bulk_service_orders_with_parent') return form
  const options = getOptions(null, form)
  const payload = form.payload || {}
  return { ...form, payload: formatInterpolatedValues(payload, options) }
}

const formatInterpolatedValues = (payload, options) => {
  if (is(Array, payload)) {
    return payload.map(item => formatInterpolatedValues(item, options))
  }

  const keys = Object.keys(payload)
  const values = Object.values(payload)
  const formattedObj = zip(keys, values).reduce((obj, [key, value]) => {
    if (!is(String, value) || !value.startsWith('::{{')) return obj
    if (!key.includes('_id')) return { ...obj, [key]: value }
    return { ...obj, [key.replace('_id', '')]: options.find(propEq('id', value)) }
  }, {})

  return { ...payload, ...formattedObj }
}
